import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import CarerCard from "@/components/CarerCard";
import BookingModal from "@/components/BookingModal";
import { Search, SlidersHorizontal, MessageCircle } from "lucide-react";

const CATS = [
  { key: "elder", label: "Elder & Disability" }, { key: "nursing", label: "Nursing & Clinical" },
  { key: "physio", label: "Physio & Speech" }, { key: "personal", label: "Personal & Daily Living" },
  { key: "mother", label: "Mother & Baby" }, { key: "mental", label: "Mental Health" },
  { key: "pet", label: "Pet Care" }, { key: "wellness", label: "Wellness & Lifestyle" },
];
const LANGS = ["Telugu", "Hindi", "English", "Tamil", "Kannada", "Malayalam", "Urdu"];

export default function FindCare() {
  const [params, setParams] = useSearchParams();
  const [carers, setCarers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);

  const cat = params.get("cat") || "";
  const q = params.get("q") || "";
  const [city, setCity] = useState("");
  const [gender, setGender] = useState("Any");
  const [minRating, setMinRating] = useState(0);
  const [maxRate, setMaxRate] = useState(2000);
  const [delivery, setDelivery] = useState("");
  const [sort, setSort] = useState("relevance");
  const [search, setSearch] = useState(q);

  const fetchCarers = useCallback(async () => {
    setLoading(true);
    const p = {};
    if (cat) p.cat = cat;
    if (q) p.q = q;
    if (city) p.city = city;
    if (gender !== "Any") p.gender = gender;
    if (minRating) p.min_rating = minRating;
    if (maxRate < 2000) p.max_rate = maxRate;
    if (delivery) p.delivery = delivery;
    const { data } = await api.get("/carers", { params: p });
    let list = [...data];
    if (sort === "rating") list.sort((a, b) => b.rating - a.rating);
    if (sort === "price_low") list.sort((a, b) => a.rate - b.rate);
    if (sort === "exp") list.sort((a, b) => b.exp_years - a.exp_years);
    setCarers(list);
    setLoading(false);
  }, [cat, q, city, gender, minRating, maxRate, delivery, sort]);

  useEffect(() => { fetchCarers(); }, [fetchCarers]);

  const setCat = (k) => { const np = new URLSearchParams(params); if (k) np.set("cat", k); else np.delete("cat"); np.delete("q"); setParams(np); };
  const doSearch = () => { const np = new URLSearchParams(); if (search) np.set("q", search); setParams(np); };

  const activeLabel = CATS.find((c) => c.key === cat)?.label;

  return (
    <PublicLayout>
      <div className="bg-ivory-2 border-b border-line">
        <div className="bmc-wrap py-6">
          <div className="bg-card rounded-full shadow-bmc border border-line-2 p-1.5 flex items-center gap-2 max-w-2xl">
            <Search size={18} className="ml-3 text-ink-faint" />
            <input value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={(e) => e.key === "Enter" && doSearch()}
              placeholder="Search by name, role or condition..." className="flex-1 bg-transparent text-sm focus:outline-none py-2" data-testid="findcare-search" />
            <button onClick={doSearch} className="btn-pill bg-coral text-white px-5 py-2 text-sm">Search</button>
          </div>
          <div className="flex gap-2 mt-4 overflow-x-auto no-scrollbar pb-1">
            <button onClick={() => setCat("")} className={`whitespace-nowrap text-sm px-4 py-2 rounded-full border transition-colors ${!cat ? "bg-coral text-white border-coral" : "border-line text-ink-soft hover:border-coral"}`} data-testid="cat-all">All</button>
            {CATS.map((c) => (
              <button key={c.key} onClick={() => setCat(c.key)} data-testid={`cat-${c.key}`}
                className={`whitespace-nowrap text-sm px-4 py-2 rounded-full border transition-colors ${cat === c.key ? "bg-coral text-white border-coral" : "border-line text-ink-soft hover:border-coral"}`}>{c.label}</button>
            ))}
          </div>
        </div>
      </div>

      <div className="bmc-wrap py-8 grid lg:grid-cols-[280px_1fr] gap-8">
        {/* Filters */}
        <aside className="space-y-6">
          <div className="flex items-center gap-2 font-display font-bold"><SlidersHorizontal size={16} /> Filters</div>

          <FilterGroup label="City">
            <Chips options={["", "Hyderabad", "Bengaluru"]} labels={["Any", "Hyderabad", "Bengaluru"]} value={city} onChange={setCity} testid="filter-city" />
          </FilterGroup>

          <FilterGroup label="Delivery mode">
            <Chips options={["", "home", "clinic", "online"]} labels={["Any", "🏠 At home", "🏥 Clinic", "💬 Online"]} value={delivery} onChange={setDelivery} testid="filter-delivery" />
          </FilterGroup>

          <FilterGroup label={`Max rate: ₹${maxRate}`}>
            <input type="range" min="200" max="2000" step="50" value={maxRate} onChange={(e) => setMaxRate(+e.target.value)} className="w-full accent-coral" data-testid="filter-rate" />
          </FilterGroup>

          <FilterGroup label="Gender">
            <Chips options={["Any", "Female", "Male"]} value={gender} onChange={setGender} testid="filter-gender" />
          </FilterGroup>

          <FilterGroup label="Min rating">
            <Chips options={[0, 4.5, 4.8, 5]} labels={["Any", "4.5+", "4.8+", "5.0"]} value={minRating} onChange={setMinRating} testid="filter-rating" />
          </FilterGroup>

          <FilterGroup label="Languages">
            <div className="flex flex-wrap gap-2">{LANGS.map((l) => <span key={l} className="text-xs border border-line rounded-full px-2.5 py-1 text-ink-soft">{l}</span>)}</div>
          </FilterGroup>
        </aside>

        {/* Results */}
        <div>
          <div className="flex flex-wrap items-center justify-between gap-3 mb-5">
            <p className="text-sm text-ink-soft"><b className="text-ink">{carers.length}</b> verified carers{activeLabel ? ` for ${activeLabel}` : ""}{city ? ` in ${city}` : ""}</p>
            <select value={sort} onChange={(e) => setSort(e.target.value)} className="text-sm border border-line rounded-full px-3 py-2 bg-card focus:outline-none" data-testid="sort-select">
              <option value="relevance">Relevance</option>
              <option value="rating">Top rated</option>
              <option value="price_low">Price: low to high</option>
              <option value="exp">Most experienced</option>
            </select>
          </div>

          {loading ? (
            <div className="text-center py-20 text-ink-soft">Loading carers...</div>
          ) : carers.length === 0 ? (
            <div className="card-bmc p-10 text-center">
              <p className="font-display font-bold text-lg">No carers match these filters</p>
              <p className="text-ink-soft text-sm mt-2">Try widening your search or message us.</p>
              <a href="https://wa.me/919000000000?text=Hi+BookMyCarer" target="_blank" rel="noreferrer" className="btn-pill bg-[#25D366] text-white px-5 py-2.5 text-sm mt-5"><MessageCircle size={15} /> WhatsApp us</a>
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 gap-5">
              {carers.map((c) => <CarerCard key={c.id} carer={c} onBook={setBooking} />)}
            </div>
          )}
        </div>
      </div>

      <BookingModal carer={booking} open={!!booking} onClose={() => setBooking(null)} />
    </PublicLayout>
  );
}

function FilterGroup({ label, children }) {
  return (
    <div className="card-bmc p-4">
      <p className="text-sm font-semibold mb-3">{label}</p>
      {children}
    </div>
  );
}

function Chips({ options, labels, value, onChange, testid }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o, i) => (
        <button key={String(o)} onClick={() => onChange(o)} data-testid={`${testid}-${o || "any"}`}
          className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${value === o ? "bg-coral text-white border-coral" : "border-line text-ink-soft hover:border-coral"}`}>
          {labels ? labels[i] : o}
        </button>
      ))}
    </div>
  );
}
